/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable @next/next/no-img-element */
import axios from "axios";
import { toBlob } from "html-to-image";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";

import Layout from "../components/Layout";
import SimpleCenteredHeader from "../components/sections/SimpleCenteredHeader";
import baseUrl from "../lib/baseUrl";
import classNames from "../lib/classNames";
import getCommonProps, { CommonProps } from "../lib/getCommonProps";

type Props = CommonProps;

type FormType = {
  doctorName: string;
  patientName: string;
  doctorComment: string;
};

const Consultation = ({ ...commonProps }: Props) => {
  const [areas, setAreas] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    51: false,
    6: false,
    7: false,
    8: false,
    9: false,
    10: false,
  });
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [captcha, setCaptcha] = useState<string | null>();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormType>();
  const headCoverageEl = useRef<HTMLDivElement>(null);

  const onSubmit = handleSubmit((data) => {
    let imageUrl = "https://ucarecdn.com/";
    toBlob(headCoverageEl.current!)
      .then((dataUrl) => {
        const formData = new FormData();
        formData.append("file", dataUrl!, `${data.patientName}.png`);
        formData.append(
          "UPLOADCARE_PUB_KEY",
          process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY!
        );
        formData.append("UPLOADCARE_STORE", "1");

        axios
          .post("https://upload.uploadcare.com/base/", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((res) => {
            imageUrl += res.data.file + "/";
            axios
              .post("https://submit-form.com/4wjpdWTM", {
                "g-recaptcha-response": captcha,
                ...data,
                image: imageUrl,
              })
              .catch((err) => {
                console.error(err);
              });
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
      });
  });

  return (
    <Layout {...commonProps}>
      <NextSeo
        title="Consultation Form"
        noindex={true}
        openGraph={{
          title: "Consultation Form",
        }}
        canonical={baseUrl + "/consultation"}
      />
      <SimpleCenteredHeader
        superTitle={"Consultation Form"}
        title={"Consultation Form"}
      />
      <div className="px-2 pt-8 mx-auto max-w-7xl">
        <form onSubmit={onSubmit}>
          <fieldset>
            <legend className="text-lg font-medium leading-6 text-gray-900">
              Personal Information
            </legend>
            <div className="grid grid-cols-1 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="doctor-name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Doctor Name{" "}
                  {errors.doctorName && (
                    <span className="text-sm text-red-500">* Required</span>
                  )}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    {...register("doctorName", { required: true })}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="patient-name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Patient Name{" "}
                  {errors.patientName && (
                    <span className="text-sm text-red-500">* Required</span>
                  )}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    autoComplete="full-name"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    {...register("patientName", { required: true })}
                  />
                </div>
              </div>

              <div className="sm:col-span-6">
                <label
                  htmlFor="about"
                  className="block text-sm font-medium text-gray-700"
                >
                  Doctor&apos;s Comment
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    defaultValue={""}
                    {...register("doctorComment")}
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset className="relative mt-10">
            <legend>Approximate Coverage</legend>

            <div
              className="flex flex-col items-center justify-center md:flex-row"
              ref={headCoverageEl}
            >
              <div className="flex justify-center md:w-full">
                <div className="absolute z-10">
                  <img
                    src="/consultation/head-left-nr.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-1"
                  className={classNames(areas[1] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-01.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-2"
                  className={classNames(areas[2] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-02.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-3"
                  className={classNames(areas[3] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-03.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-4"
                  className={classNames(areas[4] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-04.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-5"
                  className={classNames(areas[5] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-05.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-51"
                  className={classNames(areas[51] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-20.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-6"
                  className={classNames(areas[6] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-06.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-7"
                  className={classNames(areas[7] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-07.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-8"
                  className={classNames(areas[8] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-08.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-9"
                  className={classNames(areas[9] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-09.png"
                    useMap="#head_left"
                  />
                </div>
                <div
                  id="head-left-10"
                  className={classNames(areas[10] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-left-10.png"
                    useMap="#head_left"
                  />
                </div>
                <img src="/consultation/head-left-00.png" useMap="#head_left" />
                <map name="head_left">
                  <area
                    shape="poly"
                    alt=""
                    coords="224,52, 228,57, 241,75, 229,77, 220,73, 214,67, 213,66, 209,60, 208,50, 212,41, 224,52"
                    onClick={() => setAreas({ ...areas, 51: !areas[51] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="197,202, 201,196, 203,194, 206,190, 216,199, 225,207, 233,213, 227,229, 212,211, 197,202"
                    onClick={() => setAreas({ ...areas, 9: !areas[9] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="257,130, 257,131, 257,139, 254,154, 234,143, 230,136, 222,118, 234,123, 247,126, 248,126, 256,127, 257,130"
                    onClick={() => setAreas({ ...areas, 7: !areas[7] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="239,197, 236,206, 235,208, 227,202, 219,195, 209,187, 214,180, 215,172, 215,162, 215,156, 211,149, 200,141, 200,141, 188,143, 179,150, 174,160, 168,161, 162,162, 162,162, 162,160, 162,159, 160,154, 157,146, 153,140, 149,135, 145,129, 154,115, 162,107, 165,105, 173,101,
                          181,103, 192,110,
                          202,118, 209,125, 213,130, 223,140, 237,151, 253,159, 252,162, 246,181, 239,197"
                    onClick={() => setAreas({ ...areas, 10: !areas[10] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="59,74, 60,74, 59,73, 70,68, 83,70, 101,65, 114,60, 131,59, 143,58, 156,55, 157,55, 162,56, 166,57, 166,81, 161,84, 159,85, 156,76, 148,72, 136,72, 126,74, 126,74, 115,77, 101,81, 88,84, 75,85, 68,84, 61,79, 56,76, 59,74"
                    onClick={() => setAreas({ ...areas, 2: !areas[2] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="83,56, 92,46, 108,36, 127,28, 150,25, 150,26, 158,36, 165,51, 157,50, 155,51, 143,53, 131,54, 122,54, 109,56, 98,60, 87,65, 75,64, 83,56"
                    onClick={() => setAreas({ ...areas, 3: !areas[3] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="197,34, 192,58, 187,74, 181,75, 179,76, 172,78, 171,55, 165,37, 157,26, 160,26, 181,29, 197,34"
                    onClick={() => setAreas({ ...areas, 4: !areas[4] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="217,111, 212,105, 203,91, 203,90, 216,96, 234,99, 250,95, 255,122, 249,121, 248,121, 237,118, 225,114, 217,111"
                    onClick={() => setAreas({ ...areas, 6: !areas[6] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="201,84, 197,78, 192,74, 197,58, 202,36, 207,38, 203,50, 205,61, 209,69, 211,71, 219,78, 232,82, 244,79, 249,90, 233,94, 217,91, 204,85, 201,84"
                    onClick={() => setAreas({ ...areas, 5: !areas[5] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="142,123, 143,116, 146,106, 152,98, 157,93, 164,88, 173,83, 181,81, 183,80, 189,79, 193,81, 197,89, 199,93, 207,107, 215,115, 215,115, 219,124, 224,134, 216,125, 213,122, 205,115, 194,106, 184,98, 173,96, 163,100, 158,103, 151,111, 142,123"
                    onClick={() => setAreas({ ...areas, 8: !areas[8] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="155,88, 148,94, 147,95, 142,84, 133,77, 147,77, 153,80, 154,88, 155,88"
                    onClick={() => setAreas({ ...areas, 1: !areas[1] })}
                  />
                </map>
              </div>

              <div className="flex justify-center md:w-full">
                <div id="head-back-nr" className="absolute z-10">
                  <img
                    src="/consultation/head-back-nr.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-4"
                  className={classNames(areas[4] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-04.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-5"
                  className={classNames(areas[5] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-05.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-20"
                  className={classNames(areas[51] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-20.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-6"
                  className={classNames(areas[6] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-06.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-7"
                  className={classNames(areas[7] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-07.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-8"
                  className={classNames(areas[8] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-08.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-9"
                  className={classNames(areas[9] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-09.png"
                    useMap="#head_back"
                  />
                </div>
                <div
                  id="head-back-10"
                  className={classNames(areas[10] ? "" : "hidden", "absolute")}
                >
                  <img
                    src="/consultation/head-back-10.png"
                    useMap="#head_back"
                  />
                </div>
                <img src="/consultation/head-back-00.png" useMap="#head_back" />
                <map name="head_back">
                  <area
                    shape="poly"
                    alt=""
                    coords="150,40, 139,42, 129,47, 125,58, 128,69, 138,75, 150,78, 162,75, 171,69, 175,58, 171,47, 161,42, 150,40"
                    onClick={() => setAreas({ ...areas, 51: !areas[51] })}
                  />
                  <area
                    shape="rect"
                    alt=""
                    coords="85,30,215,99"
                    onClick={() => setAreas({ ...areas, 5: !areas[5] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="94,88, 109,95, 128,101, 150,104, 172,101, 191,95, 206,88, 214,84, 209,97, 195,113, 175,123, 150,127, 125,123, 105,113, 90,97, 86,84, 94,88"
                    onClick={() => setAreas({ ...areas, 6: !areas[6] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="150,249, 122,247, 88,241, 59,231, 53,204, 86,221, 113,229, 134,233, 150,233, 166,233, 187,229, 214,221, 247,204, 241,231, 212,241, 178,247, 150,249"
                    onClick={() => setAreas({ ...areas, 9: !areas[9] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="87,46, 109,33, 131,26, 150,23, 169,26, 191,33, 213,46, 227,58, 239,76, 230,74, 219,76, 220,68, 214,52, 201,40, 183,31, 165,27, 150,25, 135,27, 117,31, 99,40, 86,52, 80,68, 81,76, 70,74, 60,76, 73,58, 87,46"
                    onClick={() => setAreas({ ...areas, 4: !areas[4] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="107,141, 100,125, 94,109, 109,121, 128,129, 150,132, 172,129, 191,121, 206,109, 199,125, 193,141, 173,151, 150,155, 127,151, 107,141"
                    onClick={() => setAreas({ ...areas, 7: !areas[7] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="244,85, 232,79, 219,82, 214,105, 204,127, 200,135, 214,123, 226,113, 238,105, 251,104, 244,85"
                    onClick={() => setAreas({ ...areas, 8: !areas[8] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="81,82, 68,79, 56,85, 49,104, 61,105, 74,113, 86,123, 100,135, 96,127, 86,105, 81,82"
                    onClick={() => setAreas({ ...areas, 8: !areas[8] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="248,198, 214,215, 187,224, 166,228, 150,228, 134,228, 113,224, 86,215, 52,198, 48,167, 46,132, 48,110, 59,110, 70,116, 83,127, 101,142, 123,155, 150,160, 177,155, 199,142, 217,127, 230,116, 241,110, 252,110, 254,132, 252,167, 248,198"
                    onClick={() => setAreas({ ...areas, 10: !areas[10] })}
                  />
                </map>
              </div>

              <div className="flex justify-center md:w-full">
                <div id="head-top-nr" className="absolute z-10">
                  <img src="/consultation/head-top-nr.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-1"
                  className={classNames(areas[1] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-01.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-2"
                  className={classNames(areas[2] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-02.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-3"
                  className={classNames(areas[3] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-03.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-4"
                  className={classNames(areas[4] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-04.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-5"
                  className={classNames(areas[5] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-05.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-20"
                  className={classNames(areas[51] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-20.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-6"
                  className={classNames(areas[6] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-06.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-8"
                  className={classNames(areas[8] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-08.png" useMap="#head_top" />
                </div>
                <div
                  id="head-top-10"
                  className={classNames(areas[10] ? "" : "hidden", "absolute")}
                >
                  <img src="/consultation/head-top-10.png" useMap="#head_top" />
                </div>
                <img src="/consultation/head-top-00.png" useMap="#head_top" />

                <map name="head_top">
                  <area
                    shape="poly"
                    alt=""
                    coords="150,51, 137,54, 130,60, 127,67, 126,72, 130,81, 139,89, 150,92, 161,89, 170,81, 174,72, 173,67, 170,60, 163,54, 150,51"
                    onClick={() => setAreas({ ...areas, 51: !areas[51] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="94,188, 83,191, 74,197, 69,201, 63,218, 78,208, 93,202, 102,205, 112,211, 111,208, 109,198, 104,191, 94,188"
                    onClick={() => setAreas({ ...areas, 1: !areas[1] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="206,183, 218,186, 228,193, 224,181, 221,172, 211,167, 200,160, 192,156, 188,172, 180,192, 170,211, 160,226, 150,232, 140,226, 129,211, 120,192, 112,172, 108,156, 100,160, 89,167, 79,172, 76,181, 72,193, 82,186, 94,183, 108,187, 114,196, 116,207, 118,217, 123,229,
          132,240, 142,248, 150,251,
          158,248, 168,240, 177,229, 182,217, 184,207, 186,196, 192,187, 206,183"
                    onClick={() => setAreas({ ...areas, 2: !areas[2] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="226,197, 217,191, 206,188, 195,191, 191,198, 189,208, 188,211, 198,205, 207,202, 222,208, 237,218, 231,201, 226,197"
                    onClick={() => setAreas({ ...areas, 1: !areas[1] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="249,108, 249,108, 244,116, 240,125, 238,138, 241,157, 250,182, 252,166, 254,152, 254,152, 254,148, 249,108"
                    onClick={() => setAreas({ ...areas, 10: !areas[10] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="245,105, 248,102, 237,77, 229,94, 221,113, 216,123, 214,128, 214,135, 214,137, 216,145, 222,160, 228,179, 236,200, 242,218, 248,191, 237,161, 233,139, 235,124, 239,113, 245,105"
                    onClick={() => setAreas({ ...areas, 8: !areas[8] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="86,137, 86,135, 86,128, 84,123, 79,113, 71,94, 63,77, 52,102, 55,105, 61,113, 65,124, 67,139, 63,161, 52,191, 58,218, 64,200, 71,179, 78,160, 83,145, 86,137"
                    onClick={() => setAreas({ ...areas, 8: !areas[8] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="192,151, 194,152, 200,155, 209,160, 218,165, 211,145, 209,138, 209,135, 208,133, 192,121, 173,114, 150,112, 127,114, 108,121, 92,133, 91,135, 91,138, 89,145, 82,165, 91,160, 100,155, 106,152, 108,151, 108,151, 112,147, 122,141, 130,138, 142,141, 150,144, 158,141,
          170,138, 178,141, 188,147,
          192,151, 192,151"
                    onClick={() => setAreas({ ...areas, 4: !areas[4] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="150,33, 160,34, 172,37, 185,45, 198,59, 209,80, 216,110, 225,90, 234,72, 212,48, 189,35, 168,28, 150,26, 132,28, 110,35, 88,48, 66,72, 74,90, 83,110, 91,80, 102,59, 114,45, 128,37, 140,34, 150,33"
                    onClick={() => setAreas({ ...areas, 6: !areas[6] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="51,108, 51,108, 46,148, 46,152, 46,152, 48,166, 50,182, 59,157, 62,138, 60,125, 56,116, 51,108"
                    onClick={() => setAreas({ ...areas, 10: !areas[10] })}
                  />
                  <area
                    shape="poly"
                    alt=""
                    coords="170,143, 160,146, 150,149, 140,146, 130,143, 123,146, 115,151, 112,154, 117,170, 124,189, 133,208, 142,221, 150,227, 158,221, 167,208, 175,189, 183,170, 188,154, 185,151, 177,146, 170,143"
                    onClick={() => setAreas({ ...areas, 3: !areas[3] })}
                  />
                  <area
                    shape="rect"
                    alt=""
                    coords="87,38,213,127"
                    onClick={() => setAreas({ ...areas, 5: !areas[5] })}
                  />
                </map>
              </div>
            </div>
            <input id="zones" type="hidden" name="zones" value="" />
          </fieldset>

          {!isSubmitSuccessful ? (
            <fieldset className="flex items-center justify-end mt-10 space-x-3">
              <ReCAPTCHA
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                onChange={(response) => {
                  setCaptcha(response);
                  if (response) {
                    setButtonDisabled(false);
                  }
                }}
                onExpired={() => {
                  setCaptcha("");
                  setButtonDisabled(true);
                }}
              />
              <button
                type="submit"
                className={classNames(
                  buttonDisabled
                    ? "bg-gray-500"
                    : "bg-secondary hover:bg-lime-500 focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-none",
                  "inline-flex items-center justify-center w-full my-3 px-6 py-3 mt-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm sm:w-auto"
                )}
                disabled={buttonDisabled}
                onClick={() => setButtonDisabled(true)}
              >
                Submit
              </button>
            </fieldset>
          ) : (
            <div className="mt-10">
              <p className="text-xl text-right">
                Thank you for your submission!
              </p>
            </div>
          )}
        </form>
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const commonData = await getCommonProps(context);

  return {
    props: {
      ...commonData,
    },
  };
};

export default Consultation;
